import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const MarketIntelligence = () => {
  const [activeTab, setActiveTab] = useState('commodities');

  const commodityData = [
    { month: 'Jul', steel: 52000, copper: 68000, aluminum: 24000 },
    { month: 'Aug', steel: 54500, copper: 71000, aluminum: 25500 },
    { month: 'Sep', steel: 53000, copper: 69500, aluminum: 24800 },
    { month: 'Oct', steel: 55500, copper: 73000, aluminum: 26200 },
    { month: 'Nov', steel: 57000, copper: 75500, aluminum: 27000 },
    { month: 'Dec', steel: 58500, copper: 77000, aluminum: 27800 }
  ];

  const tradeVolumeData = [
    { region: 'Asia', volume: 425 },
    { region: 'Europe', volume: 285 },
    { region: 'Americas', volume: 95 },
    { region: 'MENA', volume: 45 }
  ];

  const insights = [
    {
      id: 1,
      title: 'Steel Prices Surge 8% in Q4 2024',
      category: 'Commodities',
      date: '2024-12-15',
      icon: 'TrendingUp',
      color: 'text-success',
      bgColor: 'bg-success/10',
      summary: 'Global steel prices show upward momentum driven by increased infrastructure spending and supply chain stabilization across major markets.'
    },
    {
      id: 2,
      title: 'New Trade Corridors Open in Southeast Asia',
      category: 'Trade Routes',
      date: '2024-12-10',
      icon: 'MapPin',
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      summary: 'Enhanced connectivity between India and ASEAN nations creates new opportunities for textile and electronics trade partnerships.'
    },
    {
      id: 3,
      title: 'Sustainability Compliance Updates',
      category: 'Regulations',
      date: '2024-12-05',
      icon: 'Shield',
      color: 'text-warning',
      bgColor: 'bg-warning/10',
      summary: 'New environmental standards for international trade require updated documentation and certification processes for cross-border shipments.'
    },
    {
      id: 4,
      title: 'Digital Trade Platforms Gain Momentum',
      category: 'Technology',
      date: '2024-11-28',
      icon: 'Zap',
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      summary: 'Blockchain-based trade finance solutions reduce transaction times by 40% and improve transparency in international commerce.'
    }
  ];

  const marketIndicators = [
    {
      name: 'Global Trade Index',
      value: '127.5',
      change: '+3.2%',
      trend: 'up',
      icon: 'TrendingUp'
    },
    {
      name: 'Freight Rates',
      value: '₹8,450/TEU',
      change: '-1.8%',
      trend: 'down',
      icon: 'Ship'
    },
    {
      name: 'Currency Exchange',
      value: '₹83.25/USD',
      change: '+0.5%',
      trend: 'up',
      icon: 'DollarSign'
    },
    {
      name: 'Trade Volume',
      value: '₹850 Cr',
      change: '+12.4%',
      trend: 'up',
      icon: 'BarChart3'
    }
  ];

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm md:text-base text-primary mb-4">
            <Icon name="BarChart3" size={20} />
            <span>Real-Time Market Intelligence</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Trade Analytics & Insights
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Stay ahead with real-time commodity pricing, market trends, and trade intelligence
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8 md:mb-12">
          {marketIndicators?.map((indicator, index) => (
            <div
              key={index}
              className="bg-card rounded-xl p-4 md:p-6 shadow-lg border border-border"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${indicator?.trend === 'up' ? 'bg-success/10' : 'bg-error/10'} flex items-center justify-center`}>
                  <Icon
                    name={indicator?.icon}
                    size={20}
                    color={indicator?.trend === 'up' ? 'var(--color-success)' : 'var(--color-error)'}
                  />
                </div>
                <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${
                  indicator?.trend === 'up' ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
                }`}>
                  <Icon name={indicator?.trend === 'up' ? 'ArrowUp' : 'ArrowDown'} size={12} />
                  {indicator?.change}
                </div>
              </div>
              <div className="text-xl md:text-2xl font-bold text-foreground mb-1">{indicator?.value}</div>
              <div className="text-xs md:text-sm text-muted-foreground">{indicator?.name}</div>
            </div>
          ))}
        </div>

        <div className="bg-card rounded-2xl p-4 md:p-6 lg:p-8 shadow-lg border border-border mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
            <h3 className="text-lg md:text-xl font-bold text-foreground">Market Trends</h3>
            <div className="flex gap-2 overflow-x-auto w-full sm:w-auto">
              <button
                onClick={() => setActiveTab('commodities')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'commodities' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Commodity Prices
              </button>
              <button
                onClick={() => setActiveTab('volume')}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                  activeTab === 'volume' ?'bg-primary text-white' :'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                Trade Volume
              </button>
            </div>
          </div>

          <div className="w-full h-64 md:h-80" aria-label={activeTab === 'commodities' ? 'Commodity Price Trends Chart' : 'Regional Trade Volume Chart'}>
            {activeTab === 'commodities' ? (
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={commodityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="month" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line type="monotone" dataKey="steel" stroke="var(--color-primary)" strokeWidth={2} name="Steel (₹/ton)" />
                  <Line type="monotone" dataKey="copper" stroke="var(--color-accent)" strokeWidth={2} name="Copper (₹/ton)" />
                  <Line type="monotone" dataKey="aluminum" stroke="var(--color-success)" strokeWidth={2} name="Aluminum (₹/ton)" />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={tradeVolumeData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                  <XAxis dataKey="region" stroke="var(--color-muted-foreground)" />
                  <YAxis stroke="var(--color-muted-foreground)" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--color-card)',
                      border: '1px solid var(--color-border)',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Bar dataKey="volume" fill="var(--color-primary)" name="Trade Volume (₹ Cr)" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h3 className="text-lg md:text-xl font-bold text-foreground mb-4">Latest Market Insights</h3>
            {insights?.map((insight) => (
              <div
                key={insight?.id}
                className="bg-card rounded-xl p-4 md:p-6 shadow-lg border border-border hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 md:w-12 md:h-12 rounded-lg ${insight?.bgColor} flex items-center justify-center flex-shrink-0`}>
                    <Icon name={insight?.icon} size={20} className={insight?.color} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <h4 className="text-sm md:text-base font-bold text-foreground line-clamp-2">{insight?.title}</h4>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{insight?.date}</span>
                    </div>
                    <p className="text-xs md:text-sm text-muted-foreground mb-3 line-clamp-2">
                      {insight?.summary}
                    </p>
                    <div className="flex items-center justify-between">
                      <span className="px-2 py-1 bg-muted text-xs text-foreground rounded-full">
                        {insight?.category}
                      </span>
                      <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                        Read More
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="space-y-6">
            <div className="bg-gradient-to-br from-primary to-secondary rounded-2xl p-6 md:p-8 text-white shadow-lg">
              <Icon name="FileText" size={40} className="mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-3">Download Market Reports</h3>
              <p className="text-sm md:text-base text-white/90 mb-6">
                Access comprehensive market analysis, trade forecasts, and industry insights compiled by our expert team.
              </p>
              <Button
                variant="default"
                size="lg"
                iconName="Download"
                iconPosition="left"
                className="bg-white text-primary hover:bg-white/90"
              >
                Get Latest Report
              </Button>
            </div>

            <div className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Bell" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-foreground">Market Alerts</h3>
                  <p className="text-sm text-muted-foreground">Stay updated instantly</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mb-6">
                Subscribe to receive real-time notifications about price changes, market opportunities, and regulatory updates.
              </p>
              <Button
                variant="outline"
                fullWidth
                iconName="Mail"
                iconPosition="left"
              >
                Subscribe to Alerts
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MarketIntelligence;